export type ReliabilityAttributes = Record<string, string | number | boolean | undefined>;

export type StartOptions = {
	attributes?: ReliabilityAttributes;
	task: string;
	taskId?: string;
	touchpointId?: string;
};

export type SucceedOptions = {
	attributes?: ReliabilityAttributes;
	task: string;
	taskId?: string;
};

export type FailOptions = {
	attributes?: ReliabilityAttributes;
	error: Error;
	task: string;
	taskId?: string;
	traceId?: string;
};

export type AbortOptions = {
	attributes?: ReliabilityAttributes;
	reason?: string;
	task: string;
	taskId?: string;
};

export type ReliabilityEvent =
	| {
			attributes?: ReliabilityAttributes;
			task: string;
			taskId?: string;
			touchpointId?: string;
			type: 'start';
	  }
	| {
			attributes?: ReliabilityAttributes;
			duration: number;
			task: string;
			taskId?: string;
			touchpointId?: string;
			type: 'succeed';
	  }
	| {
			attributes?: ReliabilityAttributes;
			duration: number;
			error: Error;
			task: string;
			taskId?: string;
			touchpointId?: string;
			traceId?: string;
			type: 'fail';
	  }
	| {
			attributes?: ReliabilityAttributes;
			duration: number;
			reason?: string;
			task: string;
			taskId?: string;
			touchpointId?: string;
			type: 'abort';
	  };

export type ReliabilitySubscriber = (event: ReliabilityEvent) => void;

type ActiveTask = {
	attributes?: ReliabilityAttributes;
	startedAt: number;
	task: string;
	taskId?: string;
	touchpointId?: string;
};

const buildKey = (task: string, taskId?: string): string =>
	taskId === undefined ? task : `${task}::${taskId}`;

export class StudioReliabilityTracker {
	private activeTasks = new Map<string, ActiveTask>();
	private subscribers = new Set<ReliabilitySubscriber>();

	subscribe(subscriber: ReliabilitySubscriber): () => void {
		this.subscribers.add(subscriber);
		return () => {
			this.subscribers.delete(subscriber);
		};
	}

	start({ task, taskId, touchpointId, attributes }: StartOptions): void {
		const key = buildKey(task, taskId);

		if (this.activeTasks.has(key)) {
			return;
		}

		this.activeTasks.set(key, {
			task,
			taskId,
			touchpointId,
			attributes,
			startedAt: performance.now(),
		});

		this.emit({ type: 'start', task, taskId, touchpointId, attributes });
	}

	succeed({ task, taskId, attributes }: SucceedOptions): void {
		const activeTask = this.consumeActiveTask(task, taskId);
		if (!activeTask) {
			return;
		}

		this.emit({
			type: 'succeed',
			task,
			taskId,
			touchpointId: activeTask.touchpointId,
			duration: performance.now() - activeTask.startedAt,
			attributes: { ...activeTask.attributes, ...attributes },
		});
	}

	fail({ task, taskId, error, traceId, attributes }: FailOptions): void {
		const activeTask = this.consumeActiveTask(task, taskId);
		if (!activeTask) {
			return;
		}

		this.emit({
			type: 'fail',
			task,
			taskId,
			touchpointId: activeTask.touchpointId,
			duration: performance.now() - activeTask.startedAt,
			error,
			traceId,
			attributes: { ...activeTask.attributes, ...attributes },
		});
	}

	abort({ task, taskId, reason, attributes }: AbortOptions): void {
		const activeTask = this.consumeActiveTask(task, taskId);
		if (!activeTask) {
			return;
		}

		this.emit({
			type: 'abort',
			task,
			taskId,
			touchpointId: activeTask.touchpointId,
			duration: performance.now() - activeTask.startedAt,
			reason,
			attributes: { ...activeTask.attributes, ...attributes },
		});
	}

	private consumeActiveTask(task: string, taskId?: string): ActiveTask | undefined {
		const key = buildKey(task, taskId);
		const activeTask = this.activeTasks.get(key);
		if (!activeTask) {
			return undefined;
		}
		this.activeTasks.delete(key);
		return activeTask;
	}

	private emit(event: ReliabilityEvent): void {
		this.subscribers.forEach((subscriber) => {
			try {
				subscriber(event);
			} catch {
				// no-op
			}
		});
	}
}

export const studioReliabilityTracker: StudioReliabilityTracker = new StudioReliabilityTracker();
